import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, TOKEN_KEY, ROLE_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export const DEV_BYPASS_ENABLED =
  process.env.REACT_APP_ENABLE_DEV_BYPASS === "true";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/loading, false = unauth
  const [loading, setLoading] = useState(true);
  // Monotonic version counter — incremented every time `setSession` runs.
  // The async `refresh` captures the version at start and skips its setUser
  // if the version changed mid-flight. Prevents the demo-tab race where the
  // initial /auth/me (sent with the parent tab's localStorage admin token)
  // would land AFTER setSession had written the demo session to sessionStorage,
  // overwriting the demo-clinic state with the admin user.
  const versionRef = useRef(0);

  const refresh = useCallback(async () => {
    const myVersion = versionRef.current;
    // Try /auth/me unconditionally: the request may authenticate via
    // (a) the httpOnly cookie set by the backend at login, or (b) the
    // legacy Authorization: Bearer header attached by the axios interceptor
    // from localStorage/sessionStorage. If neither succeeds we get a 401
    // and clear local state.
    try {
      const r = await api.get("/auth/me");
      if (versionRef.current !== myVersion) return;
      setUser(r.data);
    } catch {
      if (versionRef.current !== myVersion) return;
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(ROLE_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ROLE_KEY);
      setUser(false);
    } finally {
      if (versionRef.current === myVersion) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // setSession({ tabOnly: true }) writes to sessionStorage (per-tab) instead of
  // localStorage, so an admin previewing a demo link in a new tab doesn't lose
  // their admin session in the original tab.
  const setSession = (token, userObj, opts = {}) => {
    versionRef.current += 1;
    const store = opts.tabOnly ? sessionStorage : localStorage;
    store.setItem(TOKEN_KEY, token);
    store.setItem(ROLE_KEY, userObj.role);
    setUser(userObj);
    setLoading(false);
  };

  const logout = () => {
    versionRef.current += 1;
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    // Fire-and-forget: clear the httpOnly auth cookie on the server too.
    // The request carries the CSRF header via the axios interceptor.
    try {
      api.post("/auth/logout").catch(() => {});
    } catch (_e) { /* noop */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, setSession, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
